import { useDisclosure } from "@chakra-ui/hooks";
import { createKycVelocity, getKycDetails, updateKycVelocity } from "api/graphQL_kycvelocity";
import { Popover, PopoverTrigger, PopoverContent, PopoverHeader, PopoverArrow } from "@chakra-ui/react";
import { Modal, ModalOverlay } from "@chakra-ui/modal";
import DivLoader from "components/divloader/DivLoader";
import InputField from "components/fields/InputField";
import React, { useMemo } from "react";
import { toast } from "react-hot-toast";
import { Select as AntSelect } from "antd";
import { getCurrencySymbol, currencyCodes } from "utils/currencyList";
import { MdEdit, MdHighlightOff, MdInfo } from "react-icons/md";
import countryList from "react-select-country-list";
import {
  Accordion, AccordionItem, AccordionButton,
  AccordionPanel, AccordionIcon,
} from "@chakra-ui/react";

/* ── Section colour map ── */
const SECTION_COLORS: Record<string, { bg: string; border: string; text: string; dot: string }> = {
  no_kyc:  { bg: "linear-gradient(135deg,#f0fdf4,#dcfce7)", border: "#bbf7d0", text: "#15803d", dot: "#16a34a" },
  sdd:     { bg: "linear-gradient(135deg,#fffbeb,#fef3c7)", border: "#fde68a", text: "#b45309", dot: "#d97706" },
  edd:     { bg: "linear-gradient(135deg,#fff1f2,#ffe4e6)", border: "#fecdd3", text: "#be123c", dot: "#e11d48" },
  limits:  { bg: "linear-gradient(135deg,#eff6ff,#dbeafe)", border: "#bfdbfe", text: "#1d4ed8", dot: "#2563eb" },
  methods: { bg: "linear-gradient(135deg,#fdf4ff,#fae8ff)", border: "#e9d5ff", text: "#7c3aed", dot: "#7c3aed" },
};

const SectionHeader = ({ label, color, hasError }: { label: string; color: keyof typeof SECTION_COLORS; hasError?: boolean }) => (
  <div className="kyc-acc-header" style={{ background: SECTION_COLORS[color].bg, borderBottom: `1.5px solid ${SECTION_COLORS[color].border}` }}>
    <div className="kyc-acc-dot" style={{ background: SECTION_COLORS[color].dot }} />
    <span className="kyc-acc-label" style={{ color: SECTION_COLORS[color].text }}>{label}</span>
    {hasError && <span className="kyc-acc-err">Fill required fields</span>}
    <AccordionIcon className="kyc-acc-chevron" style={{ color: SECTION_COLORS[color].text }} />
  </div>
);

const Toggle = ({ checked, disabled, onChange }: { checked: boolean; disabled: boolean; onChange: any }) => (
  <label className="kyc-toggle">
    <input type="checkbox" className="kyc-toggle-input peer" checked={checked} disabled={disabled} onChange={onChange} />
    <div className="kyc-toggle-track peer-checked:kyc-toggle-track--on" />
  </label>
);

const CreateKycVelocityModal = ({
  fetchKycVelocity, velocityId, id, info, roleData, infoData,
}: {
  fetchKycVelocity: () => void;
  id?: string; velocityId?: any; info?: boolean; roleData?: any; infoData?: any;
}) => {
  const rootForm = {
    country: "",
    transaction_limit: { limit_no: "", period: "" },
    no_kyc: { is_active: false, max_amount_per_transaction: { amount: "", currency: "" }, min_amount_per_transaction: { amount: "", currency: "" }, kyc_count: { count: "" } },
    sdd:    { is_active: false, max_amount_per_transaction: { amount: "", currency: "" }, min_amount_per_transaction: { amount: "", currency: "" }, kyc_count: { count: "" } },
    edd:    { is_active: false, max_amount_per_transaction: { amount: "", currency: "" }, min_amount_per_transaction: { amount: "", currency: "" }, kyc_count: { count: "" } },
    max_transaction_amount: { amount: "", currency: "", period: "" },
    payment_methods: { payment: false, refund: false, apm: false, authorization: false, subscription: false, token: false, payout: false, payin: false, crypto_payin: false, crypto_payout: false },
  };

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = React.useState<boolean>();
  const [isDataLoading, setIsDataLoading] = React.useState<boolean>();
  const [formValues, setFormValues] = React.useState<any>(rootForm);
  const [viewMode, setViewMode] = React.useState<boolean>(info);
  const [formValuesErr, setFormValuesErr] = React.useState<any>({});
  const [isCloseModal, setIsCloseModal] = React.useState<boolean>(false);
  const CountryOptions = useMemo(() => countryList().getData(), []);
  const PeriodOptions = [{ label: "Day", value: "Day" }, { label: "Week", value: "Week" }, { label: "Month", value: "Month" }];
  const CurrencyOptions = currencyCodes.map((c) => { const d = getCurrencySymbol(c); return { value: c, label: `${c}: ${d.title?.toUpperCase()}` }; });
  const [popoverOpen, setPopoverOpen] = React.useState(false);
  const [popoverOpenE, setPopoverOpenE] = React.useState(false);

  const handleClose = () => {
    setFormValues(rootForm); setFormValuesErr({});
    setPopoverOpenE(false)
    setPopoverOpen(false)
    setIsCloseModal(true);
    setTimeout(() => { setIsCloseModal(false); onClose(); }, 300);
  };

  const validateData = () => {
    let verifier: any = {};
    const validateObject = (obj: any, path: string[] = []) => {
      let localErrors: any = {}; let hasError = false;
      for (const key in obj) {
        const value = obj[key]; const currentPath = [...path, key];
        if (typeof value === "string") { if (!value.trim()) { localErrors[key] = "Required"; hasError = true; } }
        else if (typeof value === "boolean") continue;
        else if (typeof value === "object" && !Array.isArray(value)) {
          const sub = validateObject(value, currentPath);
          if (Object.keys(sub).length > 0) { localErrors[key] = sub; hasError = true; }
        }
      }
      return hasError ? localErrors : {};
    };
    const anyPM = Object.values(formValues.payment_methods).some((v) => v === true);
    if (!anyPM) verifier.payment_methods = { message: "At least one payment method must be selected" };
    for (const key in formValues) {
      if (key === "payment_methods") continue;
      const value = formValues[key];
      if (["no_kyc", "sdd", "edd"].includes(key)) {
        if (value?.is_active) { const e = validateObject(value, [key]); if (Object.keys(e).length > 0) verifier[key] = e; }
        continue;
      }
      if (typeof value === "string") { if (!value.trim()) verifier[key] = "Required"; }
      else if (typeof value === "object" && !Array.isArray(value)) {
        const e = validateObject(value, [key]); if (Object.keys(e).length > 0) verifier[key] = e;
      }
    }
    setFormValuesErr(verifier);
    return Object.keys(verifier).length === 0;
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    if (!validateData()) return;
    setIsLoading(true);
    const fn = id ? updateKycVelocity(formValues, velocityId) : createKycVelocity(formValues);
    fn.then((data: any) => { toast.success(data.message); handleClose(); fetchKycVelocity(); })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const fetchKycDetails = async (id: any) => {
    setIsDataLoading(true);
    await getKycDetails(id).then((data: any) => {
      setFormValues((prev: any) => ({
        ...prev,
        country: data.country,
        transaction_limit: JSON.parse(data.transactionLimit),
        no_kyc: JSON.parse(data.noKyc),
        sdd: JSON.parse(data.sdd),
        edd: JSON.parse(data.edd),
        max_transaction_amount: JSON.parse(data.maxTransactionAmount),
        payment_methods: JSON.parse(data.paymentMethods),
      }));
      setIsDataLoading(false);
    });
  };

  /* Helper: make toggle handler for no_kyc/sdd/edd sections */
  const makeToggle = (section: "no_kyc" | "sdd" | "edd") => () => {
    const isChecked = !formValues?.[section]?.is_active;
    setFormValues((p: any) => ({ ...p, [section]: { ...p[section], is_active: isChecked, ...(!isChecked && { max_amount_per_transaction: { amount: "", currency: "" }, min_amount_per_transaction: { amount: "", currency: "" }, kyc_count: { count: "" } }) } }));
    setFormValuesErr((p: any) => { const e = { ...p }; if (!isChecked) delete e[section]; return e; });
  };

  /* Helper: amount+currency row for no_kyc/sdd/edd */
  const AmountCurrencyRow = ({ section, field, label }: { section: "no_kyc" | "sdd" | "edd"; field: "min_amount_per_transaction" | "max_amount_per_transaction"; label: string }) => {
    const active = formValues?.[section]?.is_active;
    return (
      <div className="kyc-field-group">
        <label className="kyc-field-label">{label}{active && <span className="kyc-req">*</span>}</label>
        <div className="kyc-field-row">
          <div className="kyc-field-wrap">
            <InputField variant="auth" extra="w-full" placeholder="Amount" id="amount" type="text"
              disabled={viewMode || !active} value={formValues?.[section]?.[field]?.amount}
              onChange={(e: any) => { const v = e.target.value; if (/^\d*$/.test(v)) setFormValues((p: any) => ({ ...p, [section]: { ...p[section], [field]: { ...p[section][field], amount: v } } })); }}
            />
            {formValuesErr?.[section]?.[field]?.amount && <p className="kyc-err">{formValuesErr[section][field].amount}</p>}
          </div>
          <div className="kyc-field-wrap">
            <AntSelect showSearch filterOption={(i, o) => (o?.label ?? "").toLowerCase().includes(i.toLowerCase())} allowClear placeholder="Currency" disabled={viewMode || !active}
              className="h-[48px] w-full mt-[3px]" options={CurrencyOptions}
              value={formValues?.[section]?.[field]?.currency || undefined}
              onChange={(e: any) => setFormValues((p: any) => ({ ...p, [section]: { ...p[section], [field]: { ...p[section][field], currency: e } } }))}
            />
            {formValuesErr?.[section]?.[field]?.currency && <p className="kyc-err">{formValuesErr[section][field].currency}</p>}
          </div>
        </div>
      </div>
    );
  };

  const KycCountRow = ({ section }: { section: "no_kyc" | "sdd" | "edd" }) => {
    const active = formValues?.[section]?.is_active;
    return (
      <div className="kyc-field-group">
        <label className="kyc-field-label">No. of Required Verified KYC{active && <span className="kyc-req">*</span>}</label>
        <InputField variant="auth" extra="w-full" placeholder="KYC count" id="kyc_count" type="text"
          disabled={viewMode || !active} value={formValues?.[section]?.kyc_count?.count}
          onChange={(e: any) => { const v = e.target.value; if (/^\d*$/.test(v)) setFormValues((p: any) => ({ ...p, [section]: { ...p[section], kyc_count: { ...p[section].kyc_count, count: v } } })); }}
        />
        {formValuesErr?.[section]?.kyc_count?.count && <p className="kyc-err">{formValuesErr[section].kyc_count.count}</p>}
      </div>
    );
  };

  const panelTitle = viewMode ? "View KYC Velocity" : id ? "Update KYC Velocity" : "Add KYC Velocity";

  const handleOpen = () => { if (id) fetchKycDetails(id); onOpen(); };

  return (
    <>
      {info ? (
        <Popover trigger="hover" placement="bottom" closeOnBlur  closeOnEsc isOpen={popoverOpen && !isOpen} onOpen={() => setPopoverOpen(true)} onClose={() => setPopoverOpen(false)}>
          <PopoverTrigger>
            <button className="kyc-action-icon kyc-action-icon--blue" onClick={() => { handleOpen(); setViewMode(true); }} aria-label="View" onMouseEnter={() => !isOpen && setPopoverOpen(true)} onMouseLeave={() => setPopoverOpen(false)}>
              <MdInfo size={15} />
            </button>
          </PopoverTrigger>
          <PopoverContent border="1px solid #bfdbfe" borderRadius="12px" bg="#eff6ff" maxW="160px" p="0" overflow="hidden" zIndex="20">
            <PopoverHeader bg="#dbeafe" fontSize="12px" fontWeight="700" color="#1e40af" borderBottom="1px solid #bfdbfe" px="12px" py="9px">View Velocity</PopoverHeader>
            <PopoverArrow bg="#eff6ff" />
          </PopoverContent>
        </Popover>
      ) : id ? (
        <Popover trigger="hover" placement="bottom" closeOnBlur  closeOnEsc isOpen={popoverOpenE && !isOpen} onOpen={() => setPopoverOpenE(true)} onClose={() => setPopoverOpenE(false)}>
          <PopoverTrigger>
            <button className="kyc-action-icon kyc-action-icon--amber" onClick={handleOpen} aria-label="Edit" onMouseEnter={() => !isOpen && setPopoverOpenE(true)} onMouseLeave={() => setPopoverOpenE(false)}>
              <MdEdit size={15} />
            </button>
          </PopoverTrigger>
          <PopoverContent border="1px solid #fde68a" borderRadius="12px" bg="#fffbeb" maxW="160px" p="0" overflow="hidden" zIndex="20">
            <PopoverHeader bg="#fef3c7" fontSize="12px" fontWeight="700" color="#92400e" borderBottom="1px solid #fde68a" px="12px" py="9px">Edit Velocity</PopoverHeader>
            <PopoverArrow bg="#fffbeb" />
          </PopoverContent>
        </Popover>
      ) : (
        <button className="kyc-add-btn" onClick={handleOpen}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
          </svg>
          Add KYC Velocity
        </button>
      )}

      <Modal isOpen={isOpen} onClose={() => {}}>
        <ModalOverlay bg="rgba(15,23,42,0.55)" backdropFilter="blur(4px)" />
        <div className={`kyc-panel${isOpen && !isCloseModal ? " kyc-panel--open" : " kyc-panel--close"}`}>
          <div className="kyc-panel-glow" />

          {/* Header */}
          <div className="kyc-panel-header">
            <div className="kyc-panel-brand">
              <div className="kyc-panel-logo">
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  <path d="M16 11l2 2 4-4" strokeWidth="2.5"/>
                </svg>
              </div>
              <div>
                <h2 className="kyc-panel-title">{panelTitle}</h2>
                <p className="kyc-panel-sub">{viewMode ? "Read-only view of KYC rules" : id ? "Modify velocity configuration" : "Set country-specific KYC limits"}</p>
              </div>
            </div>
            <div className="kyc-panel-header-right">
              {viewMode && roleData?.[0]?.kyc_velocity?.value?.update_kyc_velocity && (
                <button className="kyc-edit-toggle" onClick={() => setViewMode(false)}>
                  <MdEdit size={13} /> Edit
                </button>
              )}
              <button className="kyc-panel-close" onClick={handleClose}><MdHighlightOff size={22} /></button>
            </div>
          </div>

          {/* Body */}
          <div className="kyc-panel-body">
            {isDataLoading ? (
              <div className="kyc-panel-loading"><div className="kyc-spinner-sm" /><p>Loading data…</p></div>
            ) : (
              <>
                {/* Country */}
                <div className="kyc-field-group">
                  <label className="kyc-field-label">Country <span className="kyc-req">*</span></label>
                  <AntSelect showSearch filterOption={(i, o) => (o?.label ?? "").toLowerCase().includes(i.toLowerCase())}
                    disabled={viewMode} allowClear placeholder="Select Country"
                    className="h-[48px] w-full mt-[3px]"
                    options={CountryOptions?.map((m: any) => ({ ...m, label: m.label, value: m.value }))}
                    value={formValues?.country || undefined}
                    onChange={(e: any) => { setFormValues((p: any) => ({ ...p, country: e })); setFormValuesErr((p: any) => { const n = { ...p }; if (!e) n.country = "Required"; else delete n.country; return n; }); }}
                    notFoundContent="Country not found"
                  />
                  {formValuesErr?.country && <p className="kyc-err">{formValuesErr.country}</p>}
                </div>

                <div className="kyc-divider"><span className="kyc-divider-label">KYC Levels</span><div className="kyc-divider-line" /></div>

                {/* Accordion sections */}
                <Accordion allowToggle className="kyc-accordion">
                  {/* No KYC */}
                  <AccordionItem className="kyc-acc-item">
                    <AccordionButton as="div" className="kyc-acc-btn" padding="0">
                      <SectionHeader label="No KYC" color="no_kyc" hasError={!!(formValuesErr?.no_kyc)} />
                    </AccordionButton>
                    <AccordionPanel padding="0">
                      <div className="kyc-acc-body">
                        <div className="kyc-toggle-row">
                          <span className="kyc-toggle-label">Active</span>
                          <label className="kyc-toggle-wrap">
                            <input type="checkbox" className="kyc-sr-only" checked={formValues?.no_kyc?.is_active} disabled={viewMode} onChange={makeToggle("no_kyc")} />
                            <div className={`kyc-track${formValues?.no_kyc?.is_active ? " kyc-track--on" : ""}`} />
                          </label>
                        </div>
                        <AmountCurrencyRow section="no_kyc" field="min_amount_per_transaction" label="Min Amount Per Transaction" />
                        <AmountCurrencyRow section="no_kyc" field="max_amount_per_transaction" label="Max Amount Per Transaction" />
                        <KycCountRow section="no_kyc" />
                      </div>
                    </AccordionPanel>
                  </AccordionItem>

                  {/* SDD */}
                  <AccordionItem className="kyc-acc-item">
                    <AccordionButton as="div" className="kyc-acc-btn" padding="0">
                      <SectionHeader label="SDD (Simplified Due Diligence)" color="sdd" hasError={!!(formValuesErr?.sdd)} />
                    </AccordionButton>
                    <AccordionPanel padding="0">
                      <div className="kyc-acc-body">
                        <div className="kyc-toggle-row">
                          <span className="kyc-toggle-label">Active</span>
                          <label className="kyc-toggle-wrap">
                            <input type="checkbox" className="kyc-sr-only" checked={formValues?.sdd?.is_active} disabled={viewMode} onChange={makeToggle("sdd")} />
                            <div className={`kyc-track${formValues?.sdd?.is_active ? " kyc-track--on" : ""}`} />
                          </label>
                        </div>
                        <AmountCurrencyRow section="sdd" field="min_amount_per_transaction" label="Min Amount Per Transaction" />
                        <AmountCurrencyRow section="sdd" field="max_amount_per_transaction" label="Max Amount Per Transaction" />
                        <KycCountRow section="sdd" />
                      </div>
                    </AccordionPanel>
                  </AccordionItem>

                  {/* EDD */}
                  <AccordionItem className="kyc-acc-item">
                    <AccordionButton as="div" className="kyc-acc-btn" padding="0">
                      <SectionHeader label="EDD (Enhanced Due Diligence)" color="edd" hasError={!!(formValuesErr?.edd)} />
                    </AccordionButton>
                    <AccordionPanel padding="0">
                      <div className="kyc-acc-body">
                        <div className="kyc-toggle-row">
                          <span className="kyc-toggle-label">Active</span>
                          <label className="kyc-toggle-wrap">
                            <input type="checkbox" className="kyc-sr-only" checked={formValues?.edd?.is_active} disabled={viewMode} onChange={makeToggle("edd")} />
                            <div className={`kyc-track${formValues?.edd?.is_active ? " kyc-track--on" : ""}`} />
                          </label>
                        </div>
                        <AmountCurrencyRow section="edd" field="min_amount_per_transaction" label="Min Amount Per Transaction" />
                        <AmountCurrencyRow section="edd" field="max_amount_per_transaction" label="Max Amount Per Transaction" />
                        <KycCountRow section="edd" />
                      </div>
                    </AccordionPanel>
                  </AccordionItem>

                  {/* Payout Limits */}
                  <AccordionItem className="kyc-acc-item">
                    <AccordionButton as="div" className="kyc-acc-btn" padding="0">
                      <SectionHeader label="Payout Limits *" color="limits" hasError={!!(formValuesErr?.max_transaction_amount || formValuesErr?.transaction_limit)} />
                    </AccordionButton>
                    <AccordionPanel padding="0">
                      <div className="kyc-acc-body">
                        <div className="kyc-field-group">
                          <label className="kyc-field-label">Transaction Limit <span className="kyc-req">*</span></label>
                          <div className="kyc-field-row">
                            <div className="kyc-field-wrap">
                              <InputField variant="auth" extra="w-full" placeholder="Limit No." id="limit_no" type="text" disabled={viewMode}
                                value={formValues?.transaction_limit?.limit_no}
                                onChange={(e: any) => { const v = e.target.value; if (/^\d*$/.test(v)) setFormValues((p: any) => ({ ...p, transaction_limit: { ...p.transaction_limit, limit_no: v } })); }}
                              />
                              {formValuesErr?.transaction_limit?.limit_no && <p className="kyc-err">{formValuesErr.transaction_limit.limit_no}</p>}
                            </div>
                            <div className="kyc-field-wrap">
                              <AntSelect showSearch allowClear placeholder="Period" disabled={viewMode} className="h-[48px] w-full mt-[3px]" options={PeriodOptions}
                                value={formValues?.transaction_limit?.period || undefined}
                                onChange={(e: any) => setFormValues((p: any) => ({ ...p, transaction_limit: { ...p.transaction_limit, period: e } }))}
                              />
                              {formValuesErr?.transaction_limit?.period && <p className="kyc-err">{formValuesErr.transaction_limit.period}</p>}
                            </div>
                          </div>
                        </div>
                        <div className="kyc-field-group">
                          <label className="kyc-field-label">Max Transaction Amount <span className="kyc-req">*</span></label>
                          <div className="kyc-field-row">
                            <div className="kyc-field-wrap">
                              <InputField variant="auth" extra="w-full" placeholder="Amount" id="amount" type="text" disabled={viewMode}
                                value={formValues?.max_transaction_amount?.amount}
                                onChange={(e: any) => { const v = e.target.value; if (/^\d*$/.test(v)) setFormValues((p: any) => ({ ...p, max_transaction_amount: { ...p.max_transaction_amount, amount: v } })); }}
                              />
                              {formValuesErr?.max_transaction_amount?.amount && <p className="kyc-err">{formValuesErr.max_transaction_amount.amount}</p>}
                            </div>
                            <div className="kyc-field-wrap">
                              <AntSelect showSearch allowClear placeholder="Currency" disabled={viewMode} className="h-[48px] w-full mt-[3px]" options={CurrencyOptions}
                                value={formValues?.max_transaction_amount?.currency || undefined}
                                onChange={(e: any) => setFormValues((p: any) => ({ ...p, max_transaction_amount: { ...p.max_transaction_amount, currency: e } }))}
                              />
                              {formValuesErr?.max_transaction_amount?.currency && <p className="kyc-err">{formValuesErr.max_transaction_amount.currency}</p>}
                            </div>
                            <div className="kyc-field-wrap">
                              <AntSelect showSearch allowClear placeholder="Period" disabled={viewMode} className="h-[48px] w-full mt-[3px]" options={PeriodOptions}
                                value={formValues?.max_transaction_amount?.period || undefined}
                                onChange={(e: any) => setFormValues((p: any) => ({ ...p, max_transaction_amount: { ...p.max_transaction_amount, period: e } }))}
                              />
                              {formValuesErr?.max_transaction_amount?.period && <p className="kyc-err">{formValuesErr.max_transaction_amount.period}</p>}
                            </div>
                          </div>
                        </div>
                      </div>
                    </AccordionPanel>
                  </AccordionItem>

                  {/* Payment Methods */}
                  <AccordionItem className="kyc-acc-item">
                    <AccordionButton as="div" className="kyc-acc-btn" padding="0">
                      <SectionHeader label="Payment Methods *" color="methods" hasError={!!formValuesErr?.payment_methods?.message} />
                    </AccordionButton>
                    <AccordionPanel padding="0">
                      <div className="kyc-acc-body">
                        {formValuesErr?.payment_methods?.message && (
                          <p className="kyc-pm-err">{formValuesErr.payment_methods.message}</p>
                        )}
                        <div className="kyc-pm-grid">
                          {[
                            { id: "payment", label: "Payment" }, { id: "refund", label: "Refund" },
                            { id: "apm", label: "APM" }, { id: "authorization", label: "Authorization" },
                            { id: "subscription", label: "Subscription" }, { id: "token", label: "Token" },
                            { id: "payout", label: "Payout" }, { id: "payin", label: "Payin" },
                            { id: "crypto_payout", label: "Crypto Payout" }, { id: "crypto_payin", label: "Crypto Payin" },
                          ].map((item) => {
                            const active = formValues?.payment_methods?.[item.id];
                            return (
                              <div
                                key={item.id}
                                className={`kyc-pm-card${active ? " kyc-pm-card--on" : ""}${!viewMode ? " kyc-pm-card--click" : ""}`}
                                onClick={() => {
                                  if (viewMode) return;
                                  const newVal = !active;
                                  setFormValues((p: any) => {
                                    const updated = { ...p.payment_methods, [item.id]: newVal };
                                    if (Object.values(updated).some((v) => v === true)) {
                                      setFormValuesErr((e: any) => { const n = { ...e }; delete n.payment_methods; return n; });
                                    } else {
                                      setFormValuesErr((e: any) => ({ ...e, payment_methods: { message: "At least one payment method must be selected" } }));
                                    }
                                    return { ...p, payment_methods: updated };
                                  });
                                }}
                              >
                                <span className="kyc-pm-label">{item.label}</span>
                                <div className={`kyc-pm-check${active ? " kyc-pm-check--on" : ""}`}>
                                  {active && <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="kyc-panel-footer">
            <button className="kyc-btn-cancel" onClick={handleClose}>Cancel</button>
            {!viewMode && (
              <button className="kyc-btn-submit" onClick={handleSubmit} disabled={!!isLoading}>
                {isLoading ? <span className="kyc-spinner-btn" /> : (
                  <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>{id ? "Update" : "Save Rule"}</>
                )}
              </button>
            )}
          </div>
        </div>
      </Modal>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .kyc-add-btn { display:inline-flex; align-items:center; gap:7px; font-family:'Plus Jakarta Sans',sans-serif; font-size:13.5px; font-weight:700; color:#fff; padding:10px 20px; border-radius:10px; border:none; background:linear-gradient(135deg,#5b21b6,#7c3aed); cursor:pointer; box-shadow:0 3px 12px rgba(124,58,237,.28); transition:all .15s; }
        .kyc-add-btn:hover { transform:translateY(-1px); box-shadow:0 6px 20px rgba(124,58,237,.36); }

        .kyc-action-icon { width:30px; height:30px; border-radius:8px; display:flex; align-items:center; justify-content:center; cursor:pointer; transition:all .15s; border:1.5px solid; background:none; }
        .kyc-action-icon--blue { background:#eff6ff; border-color:#bfdbfe; color:#2563eb; }
        .kyc-action-icon--blue:hover { background:#dbeafe; }
        .kyc-action-icon--amber { background:#fffbeb; border-color:#fde68a; color:#d97706; }
        .kyc-action-icon--amber:hover { background:#fef3c7; }

        .kyc-panel { font-family:'Plus Jakarta Sans',sans-serif; position:fixed; top:0; right:0; bottom:0; width:100%; max-width:700px; background:#fff; z-index:1004; display:flex; flex-direction:column; box-shadow:-12px 0 60px rgba(15,23,42,.18); transition:transform .35s cubic-bezier(0.16,1,0.3,1); overflow:hidden; }
        .kyc-panel--open  { transform:translateX(0); }
        .kyc-panel--close { transform:translateX(100%); }

        .kyc-panel-glow { height:3px; flex-shrink:0; background:linear-gradient(90deg,#5b21b6,#7c3aed,#a855f7,#7c3aed,#5b21b6); background-size:200% 100%; animation:kycGlow 3s linear infinite; }
        @keyframes kycGlow { 0%{background-position:0%} 100%{background-position:200%} }

        .kyc-panel-header { padding:15px 24px; background:linear-gradient(135deg,#4c1d95 0%,#6d28d9 60%,#7c3aed 100%); display:flex; align-items:center; justify-content:space-between; flex-shrink:0; }
        .kyc-panel-brand { display:flex; align-items:center; gap:13px; }
        .kyc-panel-logo { width:42px; height:42px; border-radius:12px; background:rgba(255,255,255,.15); border:1.5px solid rgba(255,255,255,.25); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .kyc-panel-title { font-size:16px; font-weight:800; color:#fff; letter-spacing:-.3px; margin-bottom:2px; line-height:1; }
        .kyc-panel-sub { font-size:12px; color:rgba(255,255,255,.55); font-weight:500; }
        .kyc-panel-header-right { display:flex; align-items:center; gap:10px; }
        .kyc-edit-toggle { display:inline-flex; align-items:center; gap:5px; font-family:'Plus Jakarta Sans',sans-serif; font-size:12px; font-weight:700; color:#fff; background:rgba(255,255,255,.14); border:1.5px solid rgba(255,255,255,.25); border-radius:8px; padding:7px 12px; cursor:pointer; transition:background .15s; }
        .kyc-edit-toggle:hover { background:rgba(255,255,255,.24); }
        .kyc-panel-close { background:none; border:none; cursor:pointer; color:rgba(255,255,255,.65); display:flex; align-items:center; transition:color .15s; }
        .kyc-panel-close:hover { color:#fff; }

        .kyc-panel-body { flex:1; overflow-y:auto; overflow-x:hidden; padding:22px 24px; display:flex; flex-direction:column; gap:14px; }
        .kyc-panel-body::-webkit-scrollbar { width:4px; }
        .kyc-panel-body::-webkit-scrollbar-thumb { background:#cbd5e1; border-radius:4px; }
        .kyc-panel-loading { display:flex; align-items:center; gap:10px; padding:32px; font-size:13px; color:#94a3b8; }
        .kyc-spinner-sm { width:22px; height:22px; border:2.5px solid #e2e8f0; border-top-color:#7c3aed; border-radius:50%; animation:kycSpin .7s linear infinite; flex-shrink:0; }

        .kyc-field-group { display:flex; flex-direction:column; gap:5px; }
        .kyc-field-label { font-size:12.5px; font-weight:700; color:#374151; }
        .kyc-req { color:#dc2626; }
        .kyc-field-row { display:grid; grid-template-columns:repeat(auto-fit,minmax(140px,1fr)); gap:10px; }
        .kyc-field-wrap { display:flex; flex-direction:column; gap:3px; }
        .kyc-err { font-size:11.5px; color:#dc2626; font-weight:500; }

        .kyc-divider { display:flex; align-items:center; gap:10px; margin-top:4px; }
        .kyc-divider-label { font-size:9.5px; font-weight:800; color:#b8c4d8; text-transform:uppercase; letter-spacing:1px; white-space:nowrap; }
        .kyc-divider-line { flex:1; height:1px; background:linear-gradient(90deg,#e4e9f4,transparent); }

        /* Accordion */
        .kyc-accordion { display:flex; flex-direction:column; gap:8px; }
        .kyc-acc-item { border:1.5px solid #e2e8f0; border-radius:12px; overflow:hidden; }
        .kyc-acc-btn { display:block; width:100%; cursor:pointer; }
        .kyc-acc-header { display:flex; align-items:center; gap:10px; padding:12px 16px; cursor:pointer; }
        .kyc-acc-dot { width:8px; height:8px; border-radius:50%; flex-shrink:0; }
        .kyc-acc-label { font-size:13px; font-weight:700; flex:1; }
        .kyc-acc-err { font-size:11px; font-weight:600; color:#dc2626; }
        .kyc-acc-chevron { margin-left:auto; }
        .kyc-acc-body { padding:16px; display:flex; flex-direction:column; gap:14px; background:#fff; }

        /* Toggle */
        .kyc-toggle-row { display:flex; align-items:center; justify-content:space-between; }
        .kyc-toggle-label { font-size:12.5px; font-weight:700; color:#374151; }
        .kyc-toggle-wrap { display:flex; align-items:center; cursor:pointer; }
        .kyc-sr-only { position:absolute; width:1px; height:1px; overflow:hidden; clip:rect(0,0,0,0); }
        .kyc-track { width:44px; height:24px; border-radius:12px; background:#d1d5db; position:relative; transition:background .2s; }
        .kyc-track::after { content:''; position:absolute; top:2px; left:2px; width:20px; height:20px; border-radius:50%; background:#fff; transition:transform .2s; box-shadow:0 1px 3px rgba(0,0,0,.2); }
        .kyc-track--on { background:#7c3aed; }
        .kyc-track--on::after { transform:translateX(20px); }

        /* Payment methods grid */
        .kyc-pm-err { font-size:12px; font-weight:600; color:#dc2626; padding:8px 12px; background:#fff7f7; border:1.5px solid #fecaca; border-radius:8px; }
        .kyc-pm-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(130px,1fr)); gap:8px; }
        .kyc-pm-card { padding:11px 13px; border-radius:10px; border:1.5px solid #e2e8f0; background:#f8fafc; display:flex; align-items:center; justify-content:space-between; gap:8px; transition:all .15s; }
        .kyc-pm-card--click { cursor:pointer; }
        .kyc-pm-card--click:hover { border-color:#c4b5fd; background:#fdf4ff; }
        .kyc-pm-card--on { border-color:#c4b5fd; background:linear-gradient(135deg,#fdf4ff,#fae8ff); }
        .kyc-pm-label { font-size:12px; font-weight:600; color:#374151; }
        .kyc-pm-card--on .kyc-pm-label { color:#6d28d9; }
        .kyc-pm-check { width:18px; height:18px; border-radius:5px; border:1.5px solid #d1d5db; background:#fff; flex-shrink:0; display:flex; align-items:center; justify-content:center; transition:all .15s; }
        .kyc-pm-check--on { background:#7c3aed; border-color:#7c3aed; }
        .kyc-pm-check svg { width:11px; height:11px; }

        /* Footer */
        .kyc-panel-footer { padding:14px 24px; border-top:1.5px solid #f1f5f9; background:#fafbfc; display:flex; align-items:center; justify-content:flex-end; gap:10px; flex-shrink:0; }
        .kyc-btn-cancel { font-family:'Plus Jakarta Sans',sans-serif; font-size:13px; font-weight:600; color:#64748b; padding:9px 16px; border-radius:10px; border:1.5px solid #e2e8f0; background:#fff; cursor:pointer; transition:all .15s; }
        .kyc-btn-cancel:hover { background:#f1f5f9; }
        .kyc-btn-submit { display:inline-flex; align-items:center; gap:7px; font-family:'Plus Jakarta Sans',sans-serif; font-size:13.5px; font-weight:700; color:#fff; padding:10px 20px; border-radius:10px; border:none; background:linear-gradient(135deg,#5b21b6,#7c3aed); cursor:pointer; box-shadow:0 4px 14px rgba(124,58,237,.28); transition:all .15s; }
        .kyc-btn-submit:hover { transform:translateY(-1px); box-shadow:0 8px 24px rgba(124,58,237,.36); }
        .kyc-btn-submit:disabled { opacity:.5; cursor:not-allowed; transform:none; }
        .kyc-spinner-btn { display:inline-block; width:14px; height:14px; border:2px solid rgba(255,255,255,.35); border-top-color:#fff; border-radius:50%; animation:kycSpin .65s linear infinite; }
        @keyframes kycSpin { to{transform:rotate(360deg)} }

        @media (max-width:640px) { .kyc-panel { max-width:100%; } .kyc-field-row { grid-template-columns:1fr; } .kyc-pm-grid { grid-template-columns:1fr 1fr; } }
      `}</style>
    </>
  );
};

export default CreateKycVelocityModal;